import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Toolbar,
  AppBar,
  IconButton,
  useMediaQuery,
  useTheme,
  Divider,
  Tooltip,
  Avatar
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Business as CompaniesIcon,
  Receipt as InvoicesIcon,
  QrCode as PaymentCodesIcon,
  PersonAdd as SignupIcon,
  BarChart as UsageIcon,
  LibraryBooks as TemplateLibraryIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  AdminPanelSettings
} from '@mui/icons-material';
import { superAdminAuthService } from '../../../services/superAdmin/superAdminAuthService';

const drawerWidth = 260;
const collapsedDrawerWidth = 72;

const menuItems = [
  { id: 'dashboard', text: 'Dashboard', icon: <DashboardIcon />, path: '/super-admin' },
  { id: 'companies', text: 'Companies', icon: <CompaniesIcon />, path: '/super-admin/companies' },
  { id: 'invoices', text: 'Invoices', icon: <InvoicesIcon />, path: '/super-admin/invoices' },
  { id: 'payment-codes', text: 'Payment Codes', icon: <PaymentCodesIcon />, path: '/super-admin/payment-codes' },
  { id: 'signup-attempts', text: 'Signup Attempts', icon: <SignupIcon />, path: '/super-admin/signup-attempts' },
  { id: 'usage', text: 'Usage Stats', icon: <UsageIcon />, path: '/super-admin/usage' },
  { id: 'template-library', text: 'Template Library', icon: <TemplateLibraryIcon />, path: '/super-admin/template-library' }
];

const SuperAdminLayout = ({ children }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [collapsed, setCollapsed] = useState(false);

  const user = superAdminAuthService.getUser();
  const currentWidth = collapsed ? collapsedDrawerWidth : drawerWidth;

  const handleToggle = () => {
    if (isMobile) {
      setDrawerOpen(!drawerOpen);
    } else {
      setCollapsed(!collapsed);
    }
  };

  const handleNav = (path) => {
    navigate(path);
    if (isMobile) setDrawerOpen(false);
  };

  const handleLogout = () => {
    superAdminAuthService.logout();
    navigate('/super-admin/login');
  };

  const isActive = (path) => {
    if (path === '/super-admin') return location.pathname === '/super-admin';
    return location.pathname.startsWith(path);
  };

  const sidebar = (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#1e293b',
      color: '#e2e8f0'
    }}>
      {/* Logo */}
      <Toolbar sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '64px !important',
        px: 2
      }}>
        {collapsed ? (
          <AdminPanelSettings sx={{ color: '#14b8a6', fontSize: 28 }} />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AdminPanelSettings sx={{ color: '#14b8a6', fontSize: 28 }} />
            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
              <span style={{ color: '#e2e8f0' }}>Easy</span>
              <span style={{ color: '#14b8a6' }}>Check</span>
              <Typography component="span" sx={{ fontSize: '0.65rem', ml: 0.5, color: '#94a3b8', fontWeight: 400 }}>
                Admin
              </Typography>
            </Typography>
          </Box>
        )}
      </Toolbar>

      <Divider sx={{ borderColor: 'rgba(148, 163, 184, 0.15)' }} />

      {/* Menu */}
      <List sx={{ flex: 1, px: 1, py: 1 }}>
        {menuItems.map((item) => (
          <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
            <Tooltip title={collapsed ? item.text : ''} placement="right">
              <ListItemButton
                selected={isActive(item.path)}
                onClick={() => handleNav(item.path)}
                sx={{
                  borderRadius: 2,
                  py: 1.2,
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  color: '#94a3b8',
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(20, 184, 166, 0.15)',
                    color: '#14b8a6',
                    '& .MuiListItemIcon-root': { color: '#14b8a6' }
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(20, 184, 166, 0.08)',
                    color: '#14b8a6',
                    '& .MuiListItemIcon-root': { color: '#14b8a6' }
                  }
                }}
              >
                <ListItemIcon sx={{
                  minWidth: collapsed ? 0 : 40,
                  justifyContent: 'center',
                  color: 'inherit'
                }}>
                  {item.icon}
                </ListItemIcon>
                {!collapsed && (
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>

      <Divider sx={{ borderColor: 'rgba(148, 163, 184, 0.15)' }} />

      {/* User + Logout */}
      <Box sx={{ p: 2 }}>
        {!collapsed && user && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Avatar sx={{ width: 32, height: 32, backgroundColor: '#14b8a6', fontSize: '0.85rem' }}>
              {user.firstName?.[0]}{user.lastName?.[0]}
            </Avatar>
            <Box sx={{ overflow: 'hidden' }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user.firstName} {user.lastName}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user.email}
              </Typography>
            </Box>
          </Box>
        )}
        <Tooltip title="Logout" placement="right">
          <ListItemButton
            onClick={handleLogout}
            sx={{
              borderRadius: 2,
              justifyContent: collapsed ? 'center' : 'flex-start',
              color: '#ef4444',
              '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.1)' }
            }}
          >
            <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, color: 'inherit', justifyContent: 'center' }}>
              <LogoutIcon />
            </ListItemIcon>
            {!collapsed && (
              <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
            )}
          </ListItemButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar */}
      <Box component="nav" sx={{ width: { md: currentWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={drawerOpen}
          onClose={handleToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: isMobile ? drawerWidth : currentWidth,
              backgroundColor: '#1e293b !important',
              backgroundImage: 'none !important',
              borderRight: '1px solid rgba(148, 163, 184, 0.1)',
              transition: 'width 0.3s ease'
            }
          }}
        >
          {sidebar}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box sx={{
        flexGrow: 1,
        width: { md: `calc(100% - ${currentWidth}px)` },
        transition: 'width 0.3s ease',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* App Bar */}
        <AppBar
          position="static"
          elevation={0}
          sx={{
            backgroundColor: '#ffffff',
            borderBottom: '1px solid #e2e8f0',
            color: '#0f172a'
          }}
        >
          <Toolbar sx={{ minHeight: '64px !important' }}>
            <IconButton onClick={handleToggle} sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
              {menuItems.find(m => isActive(m.path))?.text || 'Admin Portal'}
            </Typography>
          </Toolbar>
        </AppBar>

        {/* Content */}
        <Box component="main" sx={{
          flex: 1,
          overflow: 'auto',
          backgroundColor: '#f8fafc',
          p: 3
        }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default SuperAdminLayout;
